import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { listCustomFieldDefinitions } from 'api/gql-utils/employeeDataSettings/queries/listCustomFieldDefinitions';
import { getEffectiveAccess } from 'components/EmployeeCustomFields/access';
import { useCurrentRole } from 'components/EmployeeCustomFields/hooks/useCurrentRole';
import { AccessedFieldDefinition } from 'components/EmployeeCustomFields/types';
import { useHrisCustomFieldsFlag } from './useHrisCustomFieldsFlag';

export const SELF_ONBOARDING_CUSTOM_FIELD_DEFINITIONS = 'SELF_ONBOARDING_CUSTOM_FIELD_DEFINITIONS';

export const useAdditionalDetailsStep = () => {
  const isFlagOn = useHrisCustomFieldsFlag();
  // Right after login the loggedInUser fetch may not have resolved yet; the
  // features selector returns null until then. Treat that window as loading so
  // the step's redirect guard doesn't bounce to Review before the flag is known.
  const isFeaturesLoaded = useSelector(loggedInUserSelectors.features) !== null;
  const { roleId, isAdmin } = useCurrentRole();

  const definitionsQuery = useQuery({
    queryKey: [SELF_ONBOARDING_CUSTOM_FIELD_DEFINITIONS],
    queryFn: () => listCustomFieldDefinitions({}),
    enabled: isFlagOn,
  });

  const editableDefinitions: AccessedFieldDefinition[] = useMemo(
    () =>
      (definitionsQuery.data?.payrollCustomFieldDefinitions?.definitions ?? [])
        .map((def) => ({ ...def, effectiveAccess: getEffectiveAccess(def, roleId, isAdmin) }))
        .filter((def) => def.effectiveAccess === 'EDIT')
        // Same ordering as the settings Employee Fields page.
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [definitionsQuery.data, roleId, isAdmin],
  );

  // When the definitions query FAILS we must NOT compute shouldShow=false — that
  // would silently drop the step and let an employee finish onboarding without
  // any mandatory custom fields. Keep the step visible on error so the step can
  // render a retryable error state and block Continue until definitions load.
  const isError = isFlagOn && definitionsQuery.isError;

  return {
    shouldShowAdditionalDetailsStep:
      isFlagOn && (isError || editableDefinitions.length > 0),
    editableDefinitions,
    isLoading: !isFeaturesLoaded || (isFlagOn && definitionsQuery.isLoading),
    isError,
    refetch: definitionsQuery.refetch,
  };
};
